import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Stethoscope, Loader2, Check, Trash2, Plus, ArrowLeft, FileText } from 'lucide-react';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';

const formatCPF = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};

const emptyLoc = { place_name: '', address: '', city: '', state: '', phone: '' };

const ProfessionalRegistrationDialog = ({ open, onClose }) => {
  const [step, setStep] = useState('form'); // form | terms | success
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: '',
    cpf: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    registration_number: '',
    formation: '',
    specialtiesText: '',
    categories: [],
    locations: [{ ...emptyLoc }],
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/professional-categories`).then((r) => setCategories(r.data.categories || []));
  }, [open]);

  const toggleCategory = (id) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(id) ? f.categories.filter((x) => x !== id) : [...f.categories, id],
    }));
  };

  const updateLoc = (i, field, val) => {
    setForm((f) => {
      const locs = [...f.locations];
      locs[i] = { ...locs[i], [field]: val };
      return { ...f, locations: locs };
    });
  };

  const addLoc = () => setForm((f) => ({ ...f, locations: [...f.locations, { ...emptyLoc }] }));
  const removeLoc = (i) =>
    setForm((f) => ({ ...f, locations: f.locations.filter((_, idx) => idx !== i) }));

  const validateForm = () => {
    if (!form.name || !form.email || !form.cpf || !form.phone || !form.password) {
      toast.error('Preencha todos os dados pessoais');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('As senhas não conferem');
      return false;
    }
    if (form.password.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return false;
    }
    if (!form.registration_number || !form.formation) {
      toast.error('Informe o número de registro e a formação');
      return false;
    }
    if (form.categories.length === 0) {
      toast.error('Selecione ao menos uma categoria profissional');
      return false;
    }
    for (let i = 0; i < form.locations.length; i++) {
      const l = form.locations[i];
      if (!l.place_name || !l.address || !l.city || !l.state || !l.phone) {
        toast.error(`Preencha todos os campos do local #${i + 1}`);
        return false;
      }
    }
    return true;
  };

  const handleGoToTerms = (e) => {
    e.preventDefault();
    if (validateForm()) setStep('terms');
  };

  const handleFinalSubmit = async () => {
    if (!termsAccepted) {
      toast.error('Aceite os Termos de Uso e a LGPD para continuar');
      return;
    }
    setSubmitting(true);
    try {
      const specialties = form.specialtiesText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      await axios.post(`${API}/register/professional`, {
        name: form.name,
        cpf: form.cpf,
        phone: form.phone,
        email: form.email,
        password: form.password,
        registration_number: form.registration_number,
        formation: form.formation,
        specialties,
        categories: form.categories,
        locations: form.locations,
        terms_accepted: true,
      });
      setStep('success');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar cadastro');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('form');
    setForm({
      name: '', cpf: '', phone: '', email: '', password: '', confirmPassword: '',
      registration_number: '', formation: '', specialtiesText: '', categories: [],
      locations: [{ ...emptyLoc }],
    });
    setTermsAccepted(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-2xl p-0"
        style={{ maxHeight: '92dvh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
        data-testid="professional-reg-dialog"
      >
        <div className="px-6 py-6">
          {step === 'form' && (
            <>
              <DialogHeader className="pb-3">
                <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  <Stethoscope className="w-5 h-5 text-teal-600" /> Cadastro de Profissional de Saúde
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Após o envio, sua conta ficará aguardando validação administrativa.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleGoToTerms} className="space-y-4">
                {/* Dados Pessoais */}
                <div className="rounded-lg border border-slate-200 p-3">
                  <h4 className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Dados Pessoais</h4>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Nome completo *</Label>
                      <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="proreg-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">CPF *</Label>
                        <Input required value={form.cpf} onChange={(e) => setForm({ ...form, cpf: formatCPF(e.target.value) })} maxLength={14} placeholder="000.000.000-00" data-testid="proreg-cpf" />
                      </div>
                      <div>
                        <Label className="text-xs">Telefone *</Label>
                        <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(00) 00000-0000" data-testid="proreg-phone" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">E-mail *</Label>
                      <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="proreg-email" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Senha *</Label>
                        <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={6} data-testid="proreg-password" />
                      </div>
                      <div>
                        <Label className="text-xs">Confirmar *</Label>
                        <Input required type="password" value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} data-testid="proreg-confirm-password" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dados Profissionais */}
                <div className="rounded-lg border border-slate-200 p-3">
                  <h4 className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Dados Profissionais</h4>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Registro profissional *</Label>
                        <Input required value={form.registration_number} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} placeholder="Ex.: CREF-123456" data-testid="proreg-regnum" />
                      </div>
                      <div>
                        <Label className="text-xs">Formação *</Label>
                        <Input required value={form.formation} onChange={(e) => setForm({ ...form, formation: e.target.value })} placeholder="Ex.: Educação Física - USP" data-testid="proreg-formation" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Especialidades (separadas por vírgula)</Label>
                      <Input value={form.specialtiesText} onChange={(e) => setForm({ ...form, specialtiesText: e.target.value })} placeholder="Ex.: Personal Trainer, Musculação, Corrida" data-testid="proreg-specialties" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">Categoria profissional *</Label>
                      <div className="grid grid-cols-2 gap-1.5">
                        {categories.map((c) => (
                          <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer p-1.5 rounded hover:bg-slate-50">
                            <input
                              type="checkbox"
                              checked={form.categories.includes(c.id)}
                              onChange={() => toggleCategory(c.id)}
                              className="w-4 h-4 accent-teal-600"
                              data-testid={`proreg-cat-${c.id}`}
                            />
                            <span>{c.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Locais de Atendimento */}
                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Locais de Atendimento *</h4>
                    <Button type="button" size="sm" variant="outline" onClick={addLoc} data-testid="proreg-add-location" className="h-7 text-xs">
                      <Plus className="w-3 h-3 mr-1" /> Local
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {form.locations.map((l, i) => (
                      <div key={i} className="border border-slate-100 rounded p-2 relative">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-semibold text-slate-500">LOCAL {i + 1}</span>
                          {form.locations.length > 1 && (
                            <button type="button" onClick={() => removeLoc(i)} className="text-red-500 hover:text-red-700" data-testid={`proreg-remove-loc-${i}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Input required value={l.place_name} onChange={(e) => updateLoc(i, 'place_name', e.target.value)} placeholder="Nome da Clínica / Academia / Studio *" data-testid={`proreg-loc-${i}-name`} />
                          <Input required value={l.address} onChange={(e) => updateLoc(i, 'address', e.target.value)} placeholder="Endereço completo *" data-testid={`proreg-loc-${i}-address`} />
                          <div className="grid grid-cols-3 gap-2">
                            <div className="col-span-2">
                              <Input required value={l.city} onChange={(e) => updateLoc(i, 'city', e.target.value)} placeholder="Cidade *" data-testid={`proreg-loc-${i}-city`} />
                            </div>
                            <Input required value={l.state} onChange={(e) => updateLoc(i, 'state', e.target.value.toUpperCase())} placeholder="UF *" maxLength={2} data-testid={`proreg-loc-${i}-state`} />
                          </div>
                          <Input required value={l.phone} onChange={(e) => updateLoc(i, 'phone', e.target.value)} placeholder="Telefone do local *" data-testid={`proreg-loc-${i}-phone`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold" data-testid="proreg-next-terms">
                  Avançar para Termos & LGPD
                </Button>
              </form>
            </>
          )}

          {step === 'terms' && (
            <>
              <DialogHeader className="pb-3">
                <DialogTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  <FileText className="w-5 h-5 text-teal-600" /> Termos de Uso e LGPD
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Leia e aceite para finalizar seu cadastro
                </DialogDescription>
              </DialogHeader>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 max-h-72 overflow-y-auto text-xs text-slate-600 leading-relaxed space-y-2">
                <h3 className="font-bold text-slate-900 text-sm">TERMOS DE USO — PROFISSIONAIS DE SAÚDE VITAL PERFORMANCE</h3>
                <p><strong>1. NATUREZA DO CADASTRO</strong> — Ao cadastrar-se como profissional parceiro, você concorda em prestar serviços aos usuários dos planos VITAL PERSONAL, mediante utilização de tokens VITALPASS.</p>
                <p><strong>2. DADOS PROFISSIONAIS</strong> — Você declara que os dados de registro profissional, formação e especialidades são verdadeiros e estão em plena vigência. A Vital Performance pode solicitar comprovação a qualquer momento.</p>
                <p><strong>3. RESPONSABILIDADE TÉCNICA</strong> — Você é o único responsável pela prestação dos serviços, respondendo civil, penal e eticamente por sua atuação junto aos usuários.</p>
                <p><strong>4. REPASSE FINANCEIRO</strong> — Cada VITALPASS consumido por um usuário para solicitar seu atendimento gera direito a repasse fixo de R$ 130,00. Os repasses seguem o ciclo definido pela Vital Performance.</p>
                <p><strong>5. LGPD</strong> — Autorizo o tratamento dos meus dados pessoais e profissionais pela Vital Performance para os fins de cadastro, divulgação na rede parceira e operacionalização financeira, em conformidade com a Lei nº 13.709/2018.</p>
                <p><strong>6. VALIDAÇÃO ADMINISTRATIVA</strong> — Meu cadastro ficará com status AGUARDANDO VALIDAÇÃO até análise da equipe. A Vital Performance pode aprovar, recusar ou solicitar correções.</p>
                <p><strong>7. SUSPENSÃO/DESATIVAÇÃO</strong> — A Vital Performance pode suspender ou desativar profissionais que violem estes termos ou apresentem má conduta.</p>
              </div>
              <div className="flex items-start gap-3 mt-4 p-3 bg-teal-50 rounded-xl">
                <Checkbox id="pro-terms" checked={termsAccepted} onCheckedChange={setTermsAccepted} data-testid="proreg-terms-checkbox" />
                <label htmlFor="pro-terms" className="text-sm text-slate-700 cursor-pointer leading-relaxed">
                  Li e aceito os <strong>Termos de Uso</strong> e a <strong>Política de Privacidade / LGPD</strong>.
                </label>
              </div>
              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={() => setStep('form')} className="flex-1" data-testid="proreg-back">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
                </Button>
                <Button onClick={handleFinalSubmit} disabled={!termsAccepted || submitting} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-bold" data-testid="proreg-submit">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : 'FINALIZAR CADASTRO'}
                </Button>
              </div>
            </>
          )}

          {step === 'success' && (
            <div className="text-center py-6" data-testid="proreg-success">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-teal-600" />
              </div>
              <DialogTitle className="text-xl mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Cadastro Enviado</DialogTitle>
              <p className="text-sm text-slate-600 mb-2 leading-relaxed">
                Recebemos seus dados. Nossa equipe irá analisar e validar suas informações profissionais.
              </p>
              <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                Após aprovação, seu cadastro será ativado e você poderá acessar a plataforma VITAL PERFORMANCE.
              </p>
              <div className="mb-4 inline-block bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full">
                Aguardando Validação
              </div>
              <div>
                <Button onClick={handleClose} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="proreg-success-close-btn">
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfessionalRegistrationDialog;
